import type {
  SurveyContactKey,
  SurveyFieldDefinition,
  SurveyLikertOption,
  SurveySelectOption,
} from '@engage/types';
import { Btn, Icon, ICONS, INPUT_STYLE, Row, Section, Toggle } from '../../forms/ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../../forms/ui/tokens';

/**
 * Right-pane field editor. Common props (label, help, required, contact-key
 * tag) plus type-specific config (options, scale bounds, Likert ranks). Emits
 * partial patches the store merges into the discriminated-union field.
 */
export function FieldSettings(props: {
  field: SurveyFieldDefinition;
  onChange: (patch: Partial<SurveyFieldDefinition>) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const { field } = props;
  // contact-key tagging only makes sense for short-text + email fields.
  const canTag = field.type === 'SHORT_TEXT' || field.type === 'EMAIL';

  return (
    <div style={{ fontFamily: FORM_UI_FONT }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: C.indigo }}>
          Edit question
          <span style={{ fontSize: 10.5, color: C.faint, marginLeft: 8, fontWeight: 600 }}>{field.type}</span>
        </div>
        <button
          type="button"
          onClick={props.onClose}
          aria-label="Close"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.faint, padding: 2 }}
        >
          <Icon d={ICONS.x} size={16} color={C.faint} />
        </button>
      </div>

      <Row label="LABEL">
        <input
          value={field.label}
          onChange={(e) => props.onChange({ label: e.target.value })}
          style={INPUT_STYLE}
        />
      </Row>

      <Row label="KEY" hint="Stable identifier used in results, branching and exports. Locked after first publish.">
        <input value={field.key} disabled style={{ ...INPUT_STYLE, color: C.faint, background: C.bg }} />
      </Row>

      <Row label="HELP TEXT (OPTIONAL)">
        <input
          value={field.help_text ?? ''}
          onChange={(e) => props.onChange({ help_text: e.target.value || undefined })}
          placeholder="Shown beneath the question"
          style={INPUT_STYLE}
        />
      </Row>

      {field.type !== 'CONSENT_CHECKBOX' && (
        <Toggle
          on={field.required}
          label="Required"
          sub="Blocks page advance until answered (when this question is shown)."
          onChange={(v) => props.onChange({ required: v })}
        />
      )}

      {canTag && (
        <div style={{ marginTop: 16 }}>
          <Row
            label="CONTACT TAG (OPTIONAL)"
            hint="Tagged answers can create a NEW contact on completion. Surveys never update an existing profile."
          >
            <select
              value={field.contact_key ?? ''}
              onChange={(e) =>
                props.onChange({ contact_key: (e.target.value || undefined) as SurveyContactKey | undefined })
              }
              style={INPUT_STYLE}
            >
              <option value="">Not tagged</option>
              <option value="email">Email (match key)</option>
              <option value="first_name">First name</option>
              <option value="last_name">Last name</option>
              <option value="company">Company</option>
            </select>
          </Row>
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <TypeSpecific field={field} onChange={props.onChange} />
      </div>

      <div style={{ marginTop: 24, borderTop: `1px solid ${C.line}`, paddingTop: 16 }}>
        <Btn kind="danger" onClick={props.onRemove}>
          <Icon d={ICONS.x} size={14} color={C.danger} />
          Remove question
        </Btn>
      </div>
    </div>
  );
}

function TypeSpecific(props: {
  field: SurveyFieldDefinition;
  onChange: (patch: Partial<SurveyFieldDefinition>) => void;
}) {
  const { field, onChange } = props;
  switch (field.type) {
    case 'SHORT_TEXT':
    case 'LONG_TEXT':
      return (
        <Row label="MAX LENGTH (OPTIONAL)">
          <input
            type="number"
            value={field.max_length ?? ''}
            onChange={(e) => onChange({ max_length: numOrUndefined(e.target.value) })}
            style={INPUT_STYLE}
          />
        </Row>
      );
    case 'NUMBER':
      return (
        <Section title="NUMBER BOUNDS">
          <ThreeUp>
            <NumField label="Min" value={field.min} onChange={(v) => onChange({ min: v })} />
            <NumField label="Max" value={field.max} onChange={(v) => onChange({ max: v })} />
            <NumField label="Step" value={field.step} onChange={(v) => onChange({ step: v })} />
          </ThreeUp>
        </Section>
      );
    case 'SINGLE_SELECT':
    case 'MULTI_SELECT':
      return (
        <Section title="OPTIONS">
          <OptionsEditor options={field.options} onChange={(options) => onChange({ options })} />
        </Section>
      );
    case 'NUMERIC_SCALE':
      return (
        <Section title="SCALE">
          <ThreeUp>
            <NumField label="Min" value={field.min} onChange={(v) => onChange({ min: v ?? 0 })} />
            <NumField label="Max" value={field.max} onChange={(v) => onChange({ max: v ?? 0 })} />
            <NumField label="Step" value={field.step} onChange={(v) => onChange({ step: v })} />
          </ThreeUp>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <Row label="MIN LABEL">
              <input
                value={field.min_label ?? ''}
                onChange={(e) => onChange({ min_label: e.target.value || undefined })}
                placeholder="e.g. Not at all"
                style={INPUT_STYLE}
              />
            </Row>
            <Row label="MAX LABEL">
              <input
                value={field.max_label ?? ''}
                onChange={(e) => onChange({ max_label: e.target.value || undefined })}
                placeholder="e.g. Extremely"
                style={INPUT_STYLE}
              />
            </Row>
          </div>
        </Section>
      );
    case 'LIKERT':
      return (
        <Section title="LIKERT OPTIONS" note="Each option carries a numeric rank used for averaging.">
          <LikertEditor options={field.options} onChange={(options) => onChange({ options })} />
        </Section>
      );
    case 'NPS':
      return (
        <div style={{ fontSize: 11.5, color: C.faint, lineHeight: 1.5 }}>
          NPS is a fixed 0–10 scale. Scored as Promoters (9–10) minus Detractors (0–6).
        </div>
      );
    case 'CONSENT_CHECKBOX':
      return (
        <Row label="CONSENT TEXT (OPTIONAL)" hint="Overrides the label as the checkbox statement.">
          <textarea
            value={field.text_override ?? ''}
            onChange={(e) => onChange({ text_override: e.target.value || undefined })}
            rows={3}
            style={{ ...INPUT_STYLE, resize: 'vertical' }}
          />
        </Row>
      );
    case 'EMAIL':
    case 'PHONE':
    case 'DATE':
      return null;
  }
}

// ─── editors ────────────────────────────────────────────────────────────────

function OptionsEditor(props: {
  options: SurveySelectOption[];
  onChange: (options: SurveySelectOption[]) => void;
}) {
  const update = (i: number, patch: Partial<SurveySelectOption>) =>
    props.onChange(props.options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  const add = () =>
    props.onChange([
      ...props.options,
      { label: `Option ${props.options.length + 1}`, value: `option_${props.options.length + 1}` },
    ]);
  const remove = (i: number) => props.onChange(props.options.filter((_, idx) => idx !== i));

  return (
    <div>
      {props.options.map((o, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <input
            value={o.label}
            onChange={(e) => update(i, { label: e.target.value, value: slugifyValue(e.target.value) })}
            placeholder="Label"
            style={{ ...INPUT_STYLE, flex: 1 }}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            disabled={props.options.length <= 1}
            title="Remove option"
            style={miniBtn(props.options.length <= 1)}
          >
            <Icon d={ICONS.x} size={12} color={C.faint} sw={2.2} />
          </button>
        </div>
      ))}
      <button type="button" onClick={add} style={addBtn}>
        <Icon d={ICONS.plus} size={12} color={C.tealDark} sw={2.4} /> Add option
      </button>
    </div>
  );
}

function LikertEditor(props: {
  options: SurveyLikertOption[];
  onChange: (options: SurveyLikertOption[]) => void;
}) {
  const update = (i: number, patch: Partial<SurveyLikertOption>) =>
    props.onChange(props.options.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  const add = () =>
    props.onChange([...props.options, { label: 'New option', value: props.options.length + 1 }]);
  const remove = (i: number) => props.onChange(props.options.filter((_, idx) => idx !== i));

  return (
    <div>
      {props.options.map((o, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <input
            type="number"
            value={o.value}
            onChange={(e) => update(i, { value: Number(e.target.value) })}
            title="Rank"
            style={{ ...INPUT_STYLE, width: 64, flexShrink: 0 }}
          />
          <input
            value={o.label}
            onChange={(e) => update(i, { label: e.target.value })}
            placeholder="Label"
            style={{ ...INPUT_STYLE, flex: 1 }}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            disabled={props.options.length <= 2}
            title="Remove option"
            style={miniBtn(props.options.length <= 2)}
          >
            <Icon d={ICONS.x} size={12} color={C.faint} sw={2.2} />
          </button>
        </div>
      ))}
      <button type="button" onClick={add} style={addBtn}>
        <Icon d={ICONS.plus} size={12} color={C.tealDark} sw={2.4} /> Add option
      </button>
    </div>
  );
}

function NumField(props: { label: string; value: number | undefined; onChange: (v: number | undefined) => void }) {
  return (
    <div style={{ flex: 1 }}>
      <label style={{ fontSize: 10.5, fontWeight: 700, color: C.indigoSoft, display: 'block', marginBottom: 5 }}>
        {props.label}
      </label>
      <input
        type="number"
        value={props.value ?? ''}
        onChange={(e) => props.onChange(numOrUndefined(e.target.value))}
        style={INPUT_STYLE}
      />
    </div>
  );
}

function ThreeUp({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 8 }}>{children}</div>;
}

const addBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: `1px solid ${C.teal}`,
  background: '#fff',
  color: C.tealDark,
  borderRadius: 8,
  padding: '7px 12px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: FORM_UI_FONT,
  marginTop: 4,
};

function miniBtn(disabled: boolean): React.CSSProperties {
  return {
    width: 30,
    height: 30,
    flexShrink: 0,
    borderRadius: 8,
    border: `1px solid ${C.line}`,
    background: '#fff',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function numOrUndefined(v: string): number | undefined {
  if (v.trim() === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

function slugifyValue(label: string): string {
  return (
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 60) || 'option'
  );
}
